// Client-side STR canonicalizer — mirrors the Ruby `Str.normalize`
// (app/services/str.rb).
//
// Canonical form: "<SEC>-<TWP>N-<RNG>W" (e.g. "14-13N-19W"), where section,
// township, and range are integers with no leading zeros and the direction
// letters are uppercased.
//
// Dependency-free and never throws on garbage input — returns null instead.
// Section-less township-range handling is intentionally out of scope here; this
// mirrors only the full-STR `normalize` path the New Transaction page needs.

// Returns [section, township, range] as numeric strings, or null when the
// input does not match any supported shape.
//
// Supported input shapes (case-insensitive):
//   "14-13N-19W"          already-canonical dashed numeric
//   "Sec 14, T13N, R19W"  prose with commas / spaces
//   "Sec 14 T13N R19W"    spaced
//   "Sec 14 T13N-R19W"    hyphen between T and R
//   "Sec 14, T13N-19W"    embedded township-range (fmtSTR m2 form)
function parse(s: string): [string, string, string] | null {
  // Already-canonical / bare dashed numeric: SEC-TN-RW
  let m = s.match(/^(\d+)-(\d+)N-(\d+)W$/i)
  if (m) return [m[1], m[2], m[3]]

  // Sec 14, T13N, R19W  /  Sec 14 T13N R19W  /  Sec 14 T13N-R19W
  m = s.match(/Sec\s*(\d+)[,\s]+T(\d+)N[,\s-]+R?(\d+)W/i)
  if (m) return [m[1], m[2], m[3]]

  // Sec 14, T13N-19W  (township-range embedded after T)
  m = s.match(/Sec\s*(\d+)[,\s]+T(\d+)N-(\d+)W/i)
  if (m) return [m[1], m[2], m[3]]

  return null
}

// strSection(raw) -> section number as a string ("14" from "14-13N-19W"),
// or null when the STR is unparseable.
export function strSection(raw: string | null | undefined): string | null {
  const canonical = normalizeStr(raw)
  return canonical ? canonical.split("-")[0] : null
}

// townshipRange(raw) -> "TN-RW" ("13N-19W" from "14-13N-19W"), or null when the
// STR is unparseable. Twin of the canonical case of Ruby `Str.township_range`.
export function townshipRange(raw: string | null | undefined): string | null {
  const canonical = normalizeStr(raw)
  if (!canonical) return null
  const parts = canonical.split("-") // [sec, "TN", "RW"]
  return parts.length === 3 ? `${parts[1]}-${parts[2]}` : null
}

// normalizeStr(raw) -> "SEC-TN-RW" canonical string, or null if unparseable.
export function normalizeStr(raw: string | null | undefined): string | null {
  if (raw == null) return null

  const s = raw.trim()
  if (s === "") return null

  const parsed = parse(s)
  if (!parsed) return null

  const [sec, twp, rng] = parsed
  // parseInt drops leading zeros; components are guaranteed numeric by parse().
  return `${parseInt(sec, 10)}-${parseInt(twp, 10)}N-${parseInt(rng, 10)}W`
}
